import { CountryCode } from 'libphonenumber-js';
import { stateDistricts } from '../../data/stateDistricts';
import {
  CourtLocationForm,
  defaultCourtLocation,
  defaultReturnAddress,
  defaultSignature,
  ReturnAddress,
  ReturnAddressForm,
  SignatureForm,
  UserSignature,
} from '../../utils/constant';

const defaultCurrentUser = {
  accessToken: '',
  email: '',
  firstName: '',
  lastName: '',
  role: 'user',
  countryCode: 'US' as CountryCode,
  signature: defaultSignature,
  returnAddress: defaultReturnAddress,
  passwordUpdatedAt: '',
  courtLocation: defaultCourtLocation,
  returnAddresses: [],
  signatures: [],
  organization: {
    id: 0,
    name: '',
    type: ''
  },
  notifications: null,
  isSmsAllowed: false,
  teamRole: '',
  thirteenTrusteeAddressExclusions: null,
};

const defaultSignUpForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  isInvOrg: false,
};

const defaultSignUpFormErrors = {
  firstNameError: '',
  lastNameError: '',
  emailError: '',
  passwordError: '',
  passwordErrorType: null,
  confirmPasswordError: '',
  requestError: '',
  directPhoneNumberError: '',
  extError: '',
  cellPhoneError: '',
  agreedToTerms: ''
};

const defaultSignInForm = {
  email: '',
  password: '',
  error: '',
};

const defaultVerificationForm = {
  code: '',
  verificationCodeResend: false,
};

const defaultVerificationFormError = {
  codeError: '',
  requestError: '',
};

const defaultPasswordResetForm = {
  email: '',
  token: '',
  code: '',
  isRequested: false,
  newPassword: '',
  confirmNewPassword: '',
  isLoading: false,
};

const defaultPasswordResetFormErrors = {
  emailError: '',
  newPasswordError: '',
  confirmNewPasswordError: '',
  requestError: '',
};

const defaultResetEmailForm = {
  email: '',
  verificationCodeResend: false,
};

const defaultResetEmailFormErrors = {
  emailError: '',
  requestError: '',
};

export interface CurrentUser {
  id?: string;
  accessToken: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  teamRole: string;
  returnAddress: ReturnAddressForm;
  signature: SignatureForm;
  passwordUpdatedAt?: string;
  courtLocation: CourtLocationForm;
  countryCode: CountryCode;
  returnAddresses: ReturnAddress[];
  signatures: UserSignature[];
  cellPhoneNumber?: string;
  isSmsAllowed: boolean;
  directOfficePhoneNumber?: string;
  organization: {
    id: number;
    name: string;
    type: string;
  };
  notifications: {
    jobCompletion: {
      attachCertificate?: {
        email: boolean;
      };
      attachInvoice?: {
        email: boolean;
      };
      email: boolean;
    };
    jobOnHold: {
      email: boolean;
    };
    jobSubmission: {
      email: boolean;
    };
    paymentDeclined: {
      email: boolean;
    };
  } | null;
  thirteenTrusteeAddressExclusions: string[] | null;
}

interface DefaultSettingsFormErrors {
  requestError: string;
}

interface VerificationForm {
  code: string;
  verificationCodeResend: boolean;
}

interface VerificationFormError {
  codeError: string;
  requestError: string;
}

export interface SignUpForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  isInvOrg: boolean;
}

interface SignUpFormErrors {
  firstNameError: string;
  lastNameError: string;
  emailError: string;
  passwordError: string;
  passwordErrorType: null | string[];
  requestError: string;
}

export interface SignInForm {
  email: string;
  password: string;
  error: string;
}

export interface PasswordResetForm {
  token: string;
  code: string;
  email: string;
  isRequested: boolean;
  newPassword: string;
  confirmNewPassword: string;
  isLoading: boolean;
}

interface PasswordResetFormErrors {
  emailError: string;
  requestError: string;
  newPasswordError: string;
  confirmNewPasswordError: string;
}

export interface ResetEmailForm {
  email: string;
}

interface ResetEmailFormErrors {
  emailError: string;
  requestError: string;
}

export interface User {
  states: string[];
  signupToken: string;
  foundOrganization: {
    name: string;
    hasData: boolean | null;
  };
  finishedMatchingOrganization: boolean;
  defaultSettingsFormErrors: DefaultSettingsFormErrors;
  verificationForm: VerificationForm;
  verificationFormError: VerificationFormError;
  signUpForm: SignUpForm;
  dashboardMetrics: {
    noticesSent: number,
    jobsCompleted: number,
    minutesReclaimed: number
  }
  signUpFormErrors: SignUpFormErrors;
  signInForm: SignInForm;
  createOrganizationNetworkError: string | null;
  createOrganizationNetworkErrorStack: string[] | null;
  passwordResetForm: PasswordResetForm;
  passwordResetFormErrors: PasswordResetFormErrors;
  resetEmailForm: ResetEmailForm;
  resetEmailFormErrors: ResetEmailFormErrors;
  verificationRequested: boolean;
  isSendingVerificationCode: boolean;
  isAuthenticating: boolean;
  isVerifyingEmail: boolean;
  isVerified: boolean;
  currentUser: CurrentUser;
  hasCreatedOrganization: boolean;
  bannerStatus: null | undefined | 'disaster' | 'holiday' | 'payment_failed';
  isCreatingOrganization: boolean;
  requestToJoinOrgSent: boolean | null;
  isRequestingToJoinOrg: boolean;
  resetEmailRequested: boolean;
  isFromInvToken: boolean;
  isChangeEmailLoading: boolean;
  isPasswordResetLoading: boolean;
  userProfile: {
    firstName: string;
    lastName: string;
    cellPhoneNumber: string;
    directOfficePhoneNumber: string;
    directOfficePhoneNumberExtension: string;
    countryCode: CountryCode;
    email: string;
    organization: {
      type: string;
      name: string;
      id: string;
    };
  };
  hasFailedPayment: boolean;
}

export const initialState: User = {
  states: Object.keys(stateDistricts),
  defaultSettingsFormErrors: {
    requestError: '',
  },
  signupToken: '',
  foundOrganization: {
    name: '',
    hasData: null,
  },
  finishedMatchingOrganization: false,
  dashboardMetrics: {
    minutesReclaimed: 0,
    noticesSent: 0,
    jobsCompleted: 0
  },
  signUpForm: defaultSignUpForm,
  signUpFormErrors: defaultSignUpFormErrors,
  signInForm: defaultSignInForm,
  verificationForm: defaultVerificationForm,
  verificationFormError: defaultVerificationFormError,
  passwordResetForm: defaultPasswordResetForm,
  passwordResetFormErrors: defaultPasswordResetFormErrors,
  resetEmailForm: defaultResetEmailForm,
  resetEmailFormErrors: defaultResetEmailFormErrors,
  verificationRequested: false,
  isSendingVerificationCode: false,
  isAuthenticating: false,
  isVerifyingEmail: false,
  isVerified: true,
  currentUser: defaultCurrentUser,
  hasCreatedOrganization: false,
  bannerStatus: undefined,
  isCreatingOrganization: false,
  createOrganizationNetworkError: null,
  createOrganizationNetworkErrorStack: null,
  // TODO: isVerified: false
  requestToJoinOrgSent: null,
  isRequestingToJoinOrg: false,
  resetEmailRequested: false,
  isFromInvToken: false,
  isChangeEmailLoading: false,
  isPasswordResetLoading: false,
  userProfile: {
    firstName: '',
    lastName: '',
    email: '',
    cellPhoneNumber: '',
    directOfficePhoneNumber: '',
    countryCode: 'US',
    directOfficePhoneNumberExtension: '',
    organization: {
      type: '',
      id: '',
      name: '',
    },
  },
  hasFailedPayment: false
};
